import type { Edge, Node, NodeGroup, SequenceFragment, SequenceMessage, SequenceParticipant } from '../types';

export interface DiagramBounds {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

export interface DiagramBoundsInput {
  nodes: Node[];
  edges: Edge[];
  groups: NodeGroup[];
  participants: SequenceParticipant[];
  messages: SequenceMessage[];
  fragments: SequenceFragment[];
  seqLifelineLength: number;
}

const PARTICIPANT_HEADER_WIDTH = 140;
const PARTICIPANT_HEADER_HEIGHT = 44;
const PARTICIPANT_HEADER_Y = 40;

function includePoint(bounds: DiagramBounds, x: number, y: number): void {
  bounds.minX = Math.min(bounds.minX, x);
  bounds.maxX = Math.max(bounds.maxX, x);
  bounds.minY = Math.min(bounds.minY, y);
  bounds.maxY = Math.max(bounds.maxY, y);
}

function includeRect(bounds: DiagramBounds, x: number, y: number, width: number, height: number): void {
  includePoint(bounds, x, y);
  includePoint(bounds, x + width, y + height);
}

function toFiniteBounds(bounds: DiagramBounds): DiagramBounds | null {
  if (!Number.isFinite(bounds.minX) || !Number.isFinite(bounds.maxX) || !Number.isFinite(bounds.minY) || !Number.isFinite(bounds.maxY)) {
    return null;
  }
  return bounds;
}

export function computeLifelineBottom(
  messages: SequenceMessage[],
  fragments: SequenceFragment[],
  seqLifelineLength: number,
): number {
  const lowestMsgY = messages.length > 0
    ? Math.max(...messages.map((message) => message.y)) + 120
    : 0;
  const lowestFragY = fragments.length > 0
    ? Math.max(...fragments.map((fragment) => fragment.endY)) + 80
    : 0;
  return Math.max(seqLifelineLength, lowestMsgY, lowestFragY, 300);
}

export function computeDiagramContentBounds(input: DiagramBoundsInput): DiagramBounds | null {
  const bounds: DiagramBounds = { minX: Infinity, maxX: -Infinity, minY: Infinity, maxY: -Infinity };
  const participantById = new Map(input.participants.map((participant) => [participant.id, participant]));
  const nodeById = new Map(input.nodes.map((node) => [node.id, node]));

  input.nodes.forEach((node) => includeRect(bounds, node.position.x, node.position.y, node.size.width, node.size.height));
  input.groups.forEach((group) => includeRect(bounds, group.position.x, group.position.y, group.size.width, group.size.height));

  input.edges.forEach((edge) => {
    const source = nodeById.get(edge.sourceNodeId);
    const target = nodeById.get(edge.targetNodeId);
    if (source) includePoint(bounds, source.position.x + source.size.width / 2, source.position.y + source.size.height / 2);
    if (target) includePoint(bounds, target.position.x + target.size.width / 2, target.position.y + target.size.height / 2);
    edge.waypoints?.forEach((waypoint) => includePoint(bounds, waypoint.x, waypoint.y));
  });

  if (input.participants.length > 0) {
    const lifelineBottom = computeLifelineBottom(input.messages, input.fragments, input.seqLifelineLength);
    input.participants.forEach((participant) => {
      includeRect(bounds, participant.x, PARTICIPANT_HEADER_Y, PARTICIPANT_HEADER_WIDTH, PARTICIPANT_HEADER_HEIGHT);
      includeRect(
        bounds,
        participant.x,
        lifelineBottom,
        PARTICIPANT_HEADER_WIDTH,
        PARTICIPANT_HEADER_HEIGHT,
      );
      includePoint(bounds, participant.x + PARTICIPANT_HEADER_WIDTH / 2, PARTICIPANT_HEADER_Y + PARTICIPANT_HEADER_HEIGHT);
      includePoint(bounds, participant.x + PARTICIPANT_HEADER_WIDTH / 2, lifelineBottom);
    });
  }

  input.messages.forEach((message) => {
    const source = participantById.get(message.sourceId);
    const target = participantById.get(message.targetId);
    if (source) includePoint(bounds, source.x + PARTICIPANT_HEADER_WIDTH / 2, message.y);
    if (target) includePoint(bounds, target.x + PARTICIPANT_HEADER_WIDTH / 2, message.y);
    if (source || target) {
      const leftX = Math.min(source?.x ?? Infinity, target?.x ?? Infinity) + PARTICIPANT_HEADER_WIDTH / 2;
      const rightX = Math.max(source?.x ?? -Infinity, target?.x ?? -Infinity) + PARTICIPANT_HEADER_WIDTH / 2;
      includePoint(bounds, leftX, message.y - 42);
      includePoint(bounds, rightX, message.y + 42);
    }
  });

  input.fragments.forEach((fragment) => {
    const xs = (fragment.participantIds.length > 0 ? fragment.participantIds : input.participants.map((participant) => participant.id))
      .map((id) => participantById.get(id)?.x)
      .filter((value): value is number => typeof value === 'number');
    if (xs.length > 0) {
      const minX = Math.min(...xs);
      const maxX = Math.max(...xs) + PARTICIPANT_HEADER_WIDTH;
      includeRect(bounds, minX, fragment.startY, Math.max(1, maxX - minX), Math.max(1, fragment.endY - fragment.startY));
      if (typeof fragment.altDividerY === 'number') {
        includePoint(bounds, minX, fragment.altDividerY);
        includePoint(bounds, maxX, fragment.altDividerY);
      }
    }
  });

  return toFiniteBounds(bounds);
}

export function computeDynamicMinScale(
  bounds: DiagramBounds | null,
  viewportWidth: number,
  viewportHeight: number,
  fallbackMinScale: number,
): number {
  if (!bounds) return fallbackMinScale;
  const width = Math.max(1, bounds.maxX - bounds.minX);
  const height = Math.max(1, bounds.maxY - bounds.minY);
  const padX = Math.max(120, viewportWidth * 0.12);
  const padY = Math.max(120, viewportHeight * 0.12);
  const fitScale = Math.min(
    Math.max(1, viewportWidth - padX * 2) / width,
    Math.max(1, viewportHeight - padY * 2) / height,
  );
  if (!Number.isFinite(fitScale)) return fallbackMinScale;
  return Math.max(fallbackMinScale, Math.min(1, fitScale));
}
